import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Send, X, Megaphone, CheckCircle2, XCircle, UserCheck, Clock, RefreshCw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABEL = { reuniao: "Reunião", assembleia_geral: "Assembleia Geral" };
const REC_LABEL = { all: "Todos os sócios", direcao: "Direção / órgãos sociais", community: "Comunidade" };

const EMPTY = {
  title: "", kind: "reuniao", date: "", time: "", location: "",
  meeting_url: "", agenda: "", recipients_type: "all", community_id: "",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

const RESPONSE_BADGE = {
  yes: { label: "Confirmado", klass: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  no: { label: "Não pode", klass: "bg-red-100 text-red-800", icon: XCircle },
  proxy: { label: "Representado", klass: "bg-amber-100 text-amber-800", icon: UserCheck },
  pending: { label: "Sem resposta", klass: "bg-gray-100 text-gray-600", icon: Clock },
};

function ConvocationForm({ initial, communities, onSaved, onCancel }) {
  const [f, setF] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((c) => ({ ...c, [k]: v }));

  const save = async () => {
    if (!f.title.trim()) { toast.error("Indique o título."); return; }
    if (f.recipients_type === "community" && !f.community_id) { toast.error("Escolha a comunidade."); return; }
    setBusy(true);
    try {
      const payload = { ...f, community_id: f.recipients_type === "community" ? f.community_id : null };
      if (initial?.id) {
        await axios.put(`${API}/admin/convocations/${initial.id}`, payload, { withCredentials: true });
        toast.success("Convocatória atualizada.");
      } else {
        await axios.post(`${API}/admin/convocations`, payload, { withCredentials: true });
        toast.success("Convocatória criada — pode agora enviá-la.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a guardar.");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm";
  const lbl = "block text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1";

  return (
    <div className="bg-white border border-[hsl(var(--accent))] p-6 space-y-4" data-testid="convocation-form">
      <div className="overline">{initial?.id ? "Editar convocatória" : "Nova convocatória"}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className={lbl}>Título *</label>
          <input value={f.title} onChange={(e) => set("title", e.target.value)}
            placeholder='Ex.: "Assembleia Geral Ordinária 2026"' className={inputCls} data-testid="conv-title" />
        </div>
        <div>
          <label className={lbl}>Tipo</label>
          <select value={f.kind} onChange={(e) => set("kind", e.target.value)} className={inputCls} data-testid="conv-kind">
            <option value="reuniao">Reunião</option>
            <option value="assembleia_geral">Assembleia Geral</option>
          </select>
        </div>
        <div>
          <label className={lbl}>Destinatários</label>
          <select value={f.recipients_type} onChange={(e) => set("recipients_type", e.target.value)} className={inputCls} data-testid="conv-recipients">
            <option value="all">Todos os sócios aprovados</option>
            <option value="direcao">Só direção / órgãos sociais</option>
            <option value="community">Só uma comunidade</option>
          </select>
        </div>
        {f.recipients_type === "community" && (
          <div className="sm:col-span-2">
            <label className={lbl}>Comunidade</label>
            <select value={f.community_id || ""} onChange={(e) => set("community_id", e.target.value)} className={inputCls} data-testid="conv-community">
              <option value="">— Escolher —</option>
              {communities.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className={lbl}>Data *</label>
          <input type="date" value={f.date || ""} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="conv-date" />
        </div>
        <div>
          <label className={lbl}>Hora</label>
          <input type="time" value={f.time || ""} onChange={(e) => set("time", e.target.value)} className={inputCls} data-testid="conv-time" />
        </div>
        <div>
          <label className={lbl}>Local</label>
          <input value={f.location || ""} onChange={(e) => set("location", e.target.value)} placeholder="Morada ou sala" className={inputCls} data-testid="conv-location" />
        </div>
        <div>
          <label className={lbl}>Link de videochamada (opcional)</label>
          <input value={f.meeting_url || ""} onChange={(e) => set("meeting_url", e.target.value)} placeholder="https://meet.google.com/…" className={inputCls} data-testid="conv-meeting-url" />
        </div>
        <div className="sm:col-span-2">
          <label className={lbl}>Ordem de trabalhos</label>
          <textarea value={f.agenda || ""} onChange={(e) => set("agenda", e.target.value)} rows={5}
            placeholder={"1. Aprovação do relatório e contas\n2. …"} className={inputCls} data-testid="conv-agenda" />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={busy} className="btn-primary disabled:opacity-60" data-testid="conv-save">
          {busy ? "A guardar…" : initial?.id ? "Guardar alterações" : "Criar convocatória"}
        </button>
        <button onClick={onCancel} className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]">Cancelar</button>
      </div>
    </div>
  );
}

function ConvocationDetail({ conv, communities, onChanged, onClose }) {
  const [c, setC] = useState(conv);
  const [editing, setEditing] = useState(false);
  const [sending, setSending] = useState(false);

  const refresh = async () => {
    const r = await axios.get(`${API}/admin/convocations/${c.id}`, { withCredentials: true });
    setC(r.data);
  };

  const sendNow = async () => {
    if (!window.confirm(`Enviar a convocatória a "${REC_LABEL[c.recipients_type]}"? Esta ação envia os emails de imediato.`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/convocations/${c.id}/send`, {}, { withCredentials: true });
      setC(r.data.convocation);
      onChanged();
      toast.success(`Convocatória enviada a ${r.data.emails_sent} sócio(s).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar.");
    } finally { setSending(false); }
  };

  const remindPending = async () => {
    const n = c.counts?.pending ?? 0;
    if (!window.confirm(`Reforçar a convocatória aos ${n} sócio(s) que ainda não responderam?`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/convocations/${c.id}/remind`, {}, { withCredentials: true });
      toast.success(`Reforço enviado a ${r.data.emails_sent} sócio(s) pendente(s).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao reforçar.");
    } finally { setSending(false); }
  };

  if (editing) {
    return <ConvocationForm initial={c} communities={communities}
      onSaved={async () => { setEditing(false); await refresh(); onChanged(); }}
      onCancel={() => setEditing(false)} />;
  }

  const counts = c.counts || {};
  const recipients = c.recipients || [];

  return (
    <div className="bg-white border border-[hsl(var(--accent))] p-6 space-y-5" data-testid="convocation-detail">
      <div className="flex items-start justify-between">
        <div>
          <div className="overline">{KIND_LABEL[c.kind]} · {REC_LABEL[c.recipients_type]}{c.community_id ? ` (${c.community_id})` : ""}</div>
          <h3 className="display-heading text-2xl mt-1">{c.title}</h3>
          <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            {fmtDate(c.date)}{c.time ? ` às ${c.time}` : ""}{c.location ? ` · ${c.location}` : ""}
          </div>
        </div>
        <button onClick={onClose} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="conv-close">
          <X className="h-5 w-5" />
        </button>
      </div>

      {c.agenda && (
        <div className="bg-[hsl(var(--secondary))] border-l-2 border-[hsl(var(--accent))] p-4 text-sm whitespace-pre-wrap">{c.agenda}</div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        {c.status === "draft" ? (
          <>
            <button onClick={sendNow} disabled={sending} className="btn-primary disabled:opacity-60" data-testid="conv-send">
              <Send className="h-3.5 w-3.5" /> {sending ? "A enviar…" : "Enviar convocatória"}
            </button>
            <button onClick={() => setEditing(true)} className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))]" data-testid="conv-edit">
              Editar
            </button>
          </>
        ) : (
          <>
            <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-800">Enviada {c.sent_at ? `· ${new Date(c.sent_at).toLocaleString("pt-PT")}` : ""}</span>
            {(c.counts?.pending ?? 0) > 0 && (
              <button onClick={remindPending} disabled={sending}
                className="btn-primary disabled:opacity-60" data-testid="conv-remind-pending">
                <Send className="h-3.5 w-3.5" /> {sending ? "A enviar…" : `Reforçar pendentes (${c.counts.pending})`}
              </button>
            )}
            <button onClick={() => setEditing(true)} className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))]" data-testid="conv-edit">
              Editar detalhes
            </button>
            <button onClick={refresh} className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="conv-refresh">
              <RefreshCw className="h-3 w-3" /> Atualizar
            </button>
          </>
        )}
      </div>

      {c.status === "sent" && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="conv-counts">
            {[["yes", "Confirmados"], ["proxy", "Representados"], ["no", "Não podem"], ["pending", "Sem resposta"]].map(([k, label]) => (
              <div key={k} className={`p-3 text-center ${RESPONSE_BADGE[k].klass}`}>
                <div className="text-2xl font-semibold" data-testid={`conv-count-${k}`}>{counts[k] ?? 0}</div>
                <div className="text-[10px] uppercase tracking-[0.16em]">{label}</div>
              </div>
            ))}
          </div>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">
            {counts.total || 0} convocados · {(counts.yes || 0) + (counts.proxy || 0)} presenças asseguradas
            {c.reminder_sent_at ? " · lembrete enviado ✓" : " · lembrete automático 1 dia antes a quem não responder"}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
                  <th className="py-2 pr-3">Sócio</th>
                  <th className="py-2 pr-3">Resposta</th>
                  <th className="py-2">Quando</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map((r) => {
                  const b = RESPONSE_BADGE[r.response || "pending"];
                  return (
                    <tr key={r.member_id} className="border-b border-[hsl(var(--border))] last:border-0" data-testid={`conv-recipient-${r.member_id}`}>
                      <td className="py-2 pr-3">{r.name} <span className="text-xs text-[hsl(var(--muted-foreground))]">({r.email})</span></td>
                      <td className="py-2 pr-3">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 ${b.klass}`}>
                          <b.icon className="h-3 w-3" /> {b.label}{r.response === "proxy" && r.proxy_name ? ` por ${r.proxy_name}` : ""}
                        </span>
                      </td>
                      <td className="py-2 text-xs text-[hsl(var(--muted-foreground))]">
                        {r.responded_at ? new Date(r.responded_at).toLocaleString("pt-PT") : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminConvocations() {
  const [items, setItems] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/convocations`, { withCredentials: true });
      setItems(r.data.items || []);
    } catch { toast.error("Erro a carregar convocatórias."); }
  };

  useEffect(() => {
    load();
    axios.get(`${API}/communities`).then((r) => setCommunities(r.data.items || r.data || [])).catch(() => {});
  }, []);

  const remove = async (c) => {
    if (!window.confirm(`Apagar a convocatória "${c.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/convocations/${c.id}`, { withCredentials: true });
      toast.success("Apagada.");
      if (open?.id === c.id) setOpen(null);
      load();
    } catch { toast.error("Erro a apagar."); }
  };

  const openDetail = async (c) => {
    const r = await axios.get(`${API}/admin/convocations/${c.id}`, { withCredentials: true });
    setOpen(r.data); setCreating(false);
  };

  return (
    <div className="p-6 lg:p-10" data-testid="admin-convocations">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Reuniões &amp; Assembleias</div>
          <h1 className="display-heading text-3xl mt-1">Convocatórias.</h1>
        </div>
        {!creating && !open && (
          <button onClick={() => setCreating(true)} className="btn-primary" data-testid="conv-new">
            <Plus className="h-3.5 w-3.5" /> Nova convocatória
          </button>
        )}
      </div>

      <div className="mt-8 space-y-4">
        {creating && (
          <ConvocationForm communities={communities}
            onSaved={() => { setCreating(false); load(); }}
            onCancel={() => setCreating(false)} />
        )}
        {open && (
          <ConvocationDetail conv={open} communities={communities}
            onChanged={load} onClose={() => setOpen(null)} />
        )}
        {!open && !creating && items.length === 0 && (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Ainda sem convocatórias. Crie a primeira — os sócios respondem com 1 clique a partir do email.
          </p>
        )}
        {!open && items.map((c) => (
          <div key={c.id} className="bg-white border border-[hsl(var(--border))] p-4 flex items-start justify-between gap-3" data-testid={`convocation-${c.id}`}>
            <div className="cursor-pointer flex-1" onClick={() => openDetail(c)}>
              <div className="font-medium text-sm flex items-center gap-2">
                <Megaphone className="h-4 w-4 text-[hsl(var(--accent))]" />
                {c.title}
                <span className="text-xs text-[hsl(var(--muted-foreground))] font-normal">· {KIND_LABEL[c.kind]}</span>
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                {fmtDate(c.date)}{c.time ? ` às ${c.time}` : ""} · {REC_LABEL[c.recipients_type]}
                {c.status === "sent" && c.counts &&
                  ` · ✔ ${c.counts.yes} · rep. ${c.counts.proxy} · ✘ ${c.counts.no} · pendentes ${c.counts.pending}`}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-xs px-2 py-1 ${c.status === "sent" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                {c.status === "sent" ? "Enviada" : "Rascunho"}
              </span>
              <button onClick={() => remove(c)} className="text-[hsl(var(--muted-foreground))] hover:text-red-700" data-testid={`conv-delete-${c.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
